/**
 * Inbox (docs/ux/project-tab-spec.md §1.9): "what needs me", across projects. The triage list
 * on the left, the inspector on the right (`?i=decision:<id>` / `?i=ask:<hub id>` /
 * `?i=batch:<session>`), full screen on mobile. `/inbox/$runId` still opens the session viewer.
 */
import { useQueryClient } from '@tanstack/react-query'
import { useChildMatches, useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { AlarmClock, Package } from 'lucide-react'
import { useRef, useState } from 'react'
import { Split } from '../components/AppShell.js'
import { BuildView, FeedbackSheet } from '../components/HubCards.js'
import { InboxInspector } from '../components/inbox/InboxInspector.js'
import { InboxTriage, type OpenIntent } from '../components/inbox/InboxTriage.js'
import { SessionView } from '../components/SessionView.js'
import { Ago, Empty, errorText, SectionTitle, useToast } from '../components/ui.js'
import { useBackend } from '../lib/backend.js'
import { useReturnFocus } from '../lib/focus-return.js'
import { qk, useInboxRanked, useProjects } from '../lib/queries.js'
import type { BuildCard, RankedItem } from '../lib/types.js'

export function InboxPage() {
  const child = useChildMatches()
  const params = useParams({ strict: false }) as { runId?: string }
  const search = useSearch({ strict: false }) as { i?: string; intent?: OpenIntent }
  const nav = useNavigate()
  const open = (i: string, intent?: OpenIntent) =>
    void nav({ to: '/inbox', search: { i, ...(intent ? { intent } : {}) } as never })
  const close = () => void nav({ to: '/inbox', search: {} as never })
  useReturnFocus(search.i) // Esc / × puts focus back on the row that opened the inspector
  // Fast triage: after an answer in the inspector, the next row opens (else the list).
  const order = useRef<readonly string[]>([])
  const next = () => {
    const keys = order.current.filter((k) => !k.startsWith('batch:'))
    const i = search.i ? keys.indexOf(search.i) : -1
    const k = i >= 0 ? (keys[i + 1] ?? keys[i - 1]) : undefined
    // A card may navigate on its own (a fresh session opens in Now): never pull it back.
    setTimeout(() => {
      if (location.pathname !== '/inbox') return
      if (k) open(k)
      else close()
    }, 0)
  }
  const detail = params.runId ? (
    <SessionView
      key={params.runId}
      runId={params.runId}
      onBack={() => void nav({ to: '/inbox' })}
    />
  ) : search.i ? (
    <InboxInspector
      key={search.i}
      inspectKey={search.i}
      {...(search.intent ? { intent: search.intent } : {})}
      onClose={close}
      onDone={next}
    />
  ) : null
  return (
    <Split
      collapseWhenEmpty
      hasDetail={child.length > 0 || !!search.i}
      list={
        <InboxTriage
          {...(search.i ? { current: search.i } : {})}
          onOpen={open}
          onClose={close}
          orderRef={order}
          footer={<InboxFooter />}
        />
      }
      detail={detail}
      fallback={
        <Empty center title="Pick something to decide">
          Each decision opens with its options, evidence and a thread with the agent that asked.
        </Empty>
      }
    />
  )
}

/** Hub-snoozed items (with Wake) and the builds digest, below the queue (also Home › Needs you). */
export function InboxFooter() {
  const ranked = useInboxRanked()
  const b = useBackend()
  const qc = useQueryClient()
  const toast = useToast()
  const [showSnoozed, setShowSnoozed] = useState(false)
  const snoozed = ranked.data?.snoozed ?? []
  const digest = ranked.data?.digest ?? []
  const wake = async (it: RankedItem) => {
    try {
      await b.api.snooze(it.id, Date.now())
      await qc.invalidateQueries({ queryKey: qk.inbox })
      await qc.invalidateQueries({ queryKey: qk.ranked })
    } catch (e) {
      toast(errorText(e), 'error')
    }
  }
  return (
    <>
      {snoozed.length ? (
        <div className="px-4 pt-4">
          <button
            type="button"
            className="vk-btn vk-btn--ghost vk-btn--sm"
            aria-expanded={showSnoozed}
            onClick={() => setShowSnoozed((v) => !v)}
          >
            <AlarmClock size={14} aria-hidden="true" />
            <span>
              {showSnoozed ? 'Hide' : 'Show'} {snoozed.length} snoozed
            </span>
          </button>
        </div>
      ) : null}
      {showSnoozed && snoozed.length ? (
        <section aria-label="Snoozed" className="pb-4">
          <SectionTitle>Snoozed</SectionTitle>
          <ul className="m-0 list-none p-0">
            {snoozed.map((it) => (
              <li key={it.id} className="row !cursor-default items-center">
                <AlarmClock size={15} className="flex-none text-text-3" aria-hidden="true" />
                <span className="grid min-w-0 flex-1 gap-0.5">
                  <span className="text-[15px]">{it.title}</span>
                  <span className="row__meta">
                    until{' '}
                    {it.snoozedUntil
                      ? new Date(it.snoozedUntil).toLocaleString([], {
                          weekday: 'short',
                          hour: 'numeric',
                          minute: '2-digit',
                        })
                      : 'later'}
                  </span>
                </span>
                <button
                  type="button"
                  className="vk-btn vk-btn--ghost vk-btn--sm"
                  onClick={() => void wake(it)}
                >
                  Wake
                </button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {digest.length ? <Digest items={digest} /> : null}
    </>
  )
}

/** Builds and FYIs never interrupt: they collect here (and in the twice-daily digest push). */
function Digest({ items }: { items: RankedItem[] }) {
  const projects = useProjects()
  const [feedback, setFeedback] = useState<BuildCard | null>(null)
  const name = (id: string) => projects.data?.find((p) => p.id === id)?.name
  return (
    <section aria-labelledby="inbox-digest" className="pb-8">
      <SectionTitle>
        <span id="inbox-digest">Builds &amp; updates</span>
        <span className="text-text-3">{items.length}</span>
      </SectionTitle>
      <div className="grid grid-cols-[minmax(0,1fr)] gap-3 px-3 desktop:px-4">
        {items.map((it) => {
          const card = it.body as BuildCard | null
          if (card?.buildId)
            return (
              <BuildView
                key={it.id}
                card={card}
                {...(name(it.projectId) ? { project: name(it.projectId) as string } : {})}
                onFeedback={() => setFeedback(card)}
              />
            )
          return (
            <div key={it.id} className="row !cursor-default">
              <Package size={15} className="mt-0.5 flex-none text-text-3" aria-hidden="true" />
              <span className="grid min-w-0 flex-1 gap-0.5">
                <span className="text-[15px]">{it.title}</span>
                <span className="row__meta">
                  <Ago ts={it.createdAt} />
                </span>
              </span>
            </div>
          )
        })}
      </div>
      {feedback ? <FeedbackSheet build={feedback} onClose={() => setFeedback(null)} /> : null}
    </section>
  )
}
